'use client'

import { IconCurrentLocation } from '@macarongfactory/web-icons'
import { Divider, ListHeader, TextButton } from '@macarongfactory/web-ui'
import { useRouter } from 'next/navigation'
import { useRef } from 'react'

import { getPlaceSeoHashId } from '@/entities/place'
import { CircularProgressIndicator } from '@/shared/design-system/components'
import { APP_SCHEME } from '@/shared/lib'
import { getPlatform } from '@/shared/lib/appBridge/appBridge.utils'

import StoryCardSwiper, { type IStoryCardItem } from './StoryCardSwiper'
import { useCarStoryNearestPlacesQuery } from '../api/query'
import { carStoryTracking } from '../lib/tracking'
import { useLocationPermissionDialog, useNearestPlaceLocation } from '../model'
import { type INearestPlaceWithReview } from '../model'
import useTrackOnVisible from '../model/useTrackOnVisible'

/** 정비소 카드 로딩 자리에 표시할 컨테이너 높이(px) */
const PLACES_LOADING_HEIGHT = 250

const toStoryCardItem = (place: INearestPlaceWithReview): IStoryCardItem => ({
  id: place.serverId,
  imageUrl: place.firstImage?.url ?? place.firstImage?.thumbnailUrl ?? '',
  title: place.placeName,
  subtitle: place.latestPositiveReview.analyzedText,
})

const PopularPlacesSection = () => {
  const router = useRouter()
  const { open: openLocationPermissionDialog } = useLocationPermissionDialog()
  const { coords, regionLabel, isRequestingLocation, requestLocation } =
    useNearestPlaceLocation({
      onPermissionDenied: openLocationPermissionDialog,
    })

  const { data: nearestPlaces, isLoading: isPlacesLoading } =
    useCarStoryNearestPlacesQuery({
      latitude: coords.latitude,
      longitude: coords.longitude,
    })

  // '내 위치' 클릭 직후부터 정비소 API 응답 완료까지를 한 줄로 묶어 로딩 표시
  const isLoading = isRequestingLocation || isPlacesLoading

  const sectionRef = useRef<HTMLElement | null>(null)
  useTrackOnVisible({
    ref: sectionRef,
    tracker: carStoryTracking.showNearbyShopSection,
    enabled: (nearestPlaces?.length ?? 0) > 0,
  })

  const locationButtonRef = useRef<HTMLDivElement | null>(null)
  useTrackOnVisible({
    ref: locationButtonRef,
    tracker: carStoryTracking.showNearbyShopLocationButton,
  })

  const handleClickMyLocation = () => {
    carStoryTracking.clickNearbyShopLocationButton()
    void requestLocation()
  }

  // 정비소 카드 클릭: 인앱이면 앱스킴, 외부면 SEO 정비소 상세(place/{hashId})
  const handleSelectPlace = async (item: IStoryCardItem, index: number) => {
    carStoryTracking.clickNearbyShopCard(item.title, index)

    if (getPlatform() !== null) {
      window.location.href = APP_SCHEME.SERVICE_DETAIL(item.id)
      return
    }

    try {
      const hashId = await getPlaceSeoHashId({ serverId: item.id })
      if (hashId) router.push(`/place/${hashId}`)
    } catch {
      // hashId 인코딩 실패 시 이동하지 않음
    }
  }

  const items = nearestPlaces?.map(toStoryCardItem) ?? []

  if (!isLoading && items.length === 0) return null

  return (
    <>
      <Divider.Horizontal strokeWidth='stroke-width-x10' />
      <section ref={sectionRef} className='pt-dimension-x3 pb-dimension-x10'>
        <ListHeader
          title={
            <ListHeader.Main.Title.Text
              text={`${regionLabel} 주변 정비소`}
              typography='heading-3-bold'
            />
          }
          right={
            <ListHeader.Main.Right.Accessory>
              <div ref={locationButtonRef}>
                <TextButton
                  className='-mr-dimension-x2'
                  text='내 위치'
                  variant='trailing-icon'
                  trailingIcon={<IconCurrentLocation width={14} height={14} />}
                  typography='body-1-medium'
                  textColor='foreground-neutral-alternative'
                  iconColor='foreground-neutral-alternative'
                  clickable={handleClickMyLocation}
                />
              </div>
            </ListHeader.Main.Right.Accessory>
          }
        />
        {isLoading ? (
          <div
            className='flex items-center justify-center'
            style={{ height: PLACES_LOADING_HEIGHT }}
          >
            <CircularProgressIndicator size='medium' variant='gray' />
          </div>
        ) : (
          <StoryCardSwiper
            items={items}
            emphasis='subtitle'
            onCardShow={(item, index) =>
              carStoryTracking.showNearbyShopCard(item.title, index)
            }
            onCardSelect={handleSelectPlace}
            onFirstSwipe={carStoryTracking.scrollNearbyShop}
          />
        )}
      </section>
    </>
  )
}

export default PopularPlacesSection
